import { useSearchParams } from 'react-router-dom';

export default function SigningErrorPage() {
  const [searchParams] = useSearchParams();
  const errorType = searchParams.get('type') ?? 'not_found';
  const senderName = searchParams.get('sender') || 'the sender';
  const senderEmail = searchParams.get('email') ?? '';
  const expiryDate = searchParams.get('expiry') ?? '';

  const senderInitials = senderName === 'the sender'
    ? '?'
    : senderName.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <svg width="22" height="22" viewBox="0 0 256 256" fill="none" style={{ flexShrink: 0 }}>
            <defs>
              <linearGradient id="cornerLogoErr" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
                <stop stopColor="#D46A3A" /><stop offset="1" stopColor="#8B3A10" />
              </linearGradient>
            </defs>
            <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#cornerLogoErr)" strokeWidth="40" strokeLinecap="round" />
          </svg>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#3D3229', lineHeight: '16px', fontFamily: 'Geist, system-ui, sans-serif' }}>Corner</span>
        </div>

        {errorType === 'expired' && (
          <ExpiredContent senderName={senderName} senderEmail={senderEmail} senderInitials={senderInitials} expiryDate={expiryDate} />
        )}
        {errorType === 'revoked' && (
          <RevokedContent senderName={senderName} senderEmail={senderEmail} senderInitials={senderInitials} />
        )}
        {(errorType === 'not_found' || !['expired', 'not_found', 'revoked'].includes(errorType)) && (
          <NotFoundContent />
        )}
      </div>
    </div>
  );
}

function ExpiredContent({ senderName, senderEmail, senderInitials, expiryDate }: {
  senderName: string; senderEmail: string; senderInitials: string; expiryDate: string;
}) {
  const dateStr = expiryDate
    ? new Date(expiryDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <>
      {/* Icon + text */}
      <div style={contentCenterStyle}>
        <div style={{ ...iconCircleStyle, backgroundColor: '#FDF5EF' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="1.5" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
        </div>
        <div style={textGroupStyle}>
          <h1 style={titleStyle}>Link expired</h1>
          <p style={messageStyle}>
            This signing link {dateStr ? `expired on ${dateStr}` : 'has expired'}. Contact {senderName} to request a new link.
          </p>
        </div>
      </div>

      {/* Sender card */}
      <div style={senderCardStyle}>
        <div style={avatarStyle}>{senderInitials}</div>
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 1 }}>
          <span style={senderNameStyle}>{senderName}</span>
          {senderEmail && <span style={senderEmailStyle}>{senderEmail}</span>}
        </div>
      </div>

      {/* Status */}
      <div style={statusRowStyle}>
        <span style={statusLabelStyle}>Expired</span>
      </div>
    </>
  );
}

function NotFoundContent() {
  return (
    <>
      {/* Icon + text */}
      <div style={contentCenterStyle}>
        <div style={{ ...iconCircleStyle, backgroundColor: '#FEF2F2' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#B54B4B" strokeWidth="1.5" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" />
            <path d="M15 9l-6 6" />
            <path d="M9 9l6 6" />
          </svg>
        </div>
        <div style={textGroupStyle}>
          <h1 style={titleStyle}>Link not found</h1>
          <p style={messageStyle}>
            This signing link is invalid or no longer active. Please check the link or contact the sender for a new one.
          </p>
        </div>
      </div>

      {/* Go to Corner button */}
      <a
        href="/"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '100%', boxSizing: 'border-box' as const,
          border: '1px solid #E8DED4', borderRadius: 8, padding: '10px 20px',
          backgroundColor: 'transparent', color: '#3D3229', fontSize: 13, fontWeight: 400,
          textDecoration: 'none', fontFamily: 'Geist, system-ui, sans-serif', lineHeight: '16px',
        }}
      >
        Go to Corner
      </a>

      {/* Status */}
      <div style={statusRowStyle}>
        <span style={statusLabelStyle}>Invalid</span>
      </div>
    </>
  );
}

function RevokedContent({ senderName, senderEmail, senderInitials }: {
  senderName: string; senderEmail: string; senderInitials: string;
}) {
  return (
    <>
      {/* Icon + text */}
      <div style={contentCenterStyle}>
        <div style={{ ...iconCircleStyle, backgroundColor: '#F5F0E8' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B7D6B" strokeWidth="1.5" style={{ flexShrink: 0 }}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M9.5 9l5 5" />
            <path d="M14.5 9l-5 5" />
          </svg>
        </div>
        <div style={textGroupStyle}>
          <h1 style={titleStyle}>Invite revoked</h1>
          <p style={messageStyle}>
            The sender has cancelled this signing request. This document is no longer available for signing.
          </p>
        </div>
      </div>

      {/* Sender card */}
      <div style={senderCardStyle}>
        <div style={avatarStyle}>{senderInitials}</div>
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 1 }}>
          <span style={senderNameStyle}>{senderName}</span>
          {senderEmail && <span style={senderEmailStyle}>{senderEmail}</span>}
        </div>
      </div>

      {/* Status */}
      <div style={statusRowStyle}>
        <span style={statusLabelStyle}>Revoked</span>
      </div>
    </>
  );
}

// ─── Shared Styles ──────────────────────────────────

const pageStyle: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  backgroundColor: '#F2EFE9', fontFamily: 'Geist, system-ui, sans-serif', padding: 20,
};

const cardStyle: React.CSSProperties = {
  width: 380, backgroundColor: '#FFFFFF',
  borderRadius: 16, padding: '40px 36px',
  boxShadow: '0px 2px 16px #0000000F',
  display: 'flex', flexDirection: 'column', gap: 24,
};

const contentCenterStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  paddingBlock: 8, gap: 14, width: '100%',
};

const iconCircleStyle: React.CSSProperties = {
  width: 52, height: 52, borderRadius: 26,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
};

const textGroupStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
};

const titleStyle: React.CSSProperties = {
  margin: 0, fontSize: 22, fontWeight: 400, color: '#3D3229',
  fontFamily: '"Instrument Serif", system-ui, sans-serif', lineHeight: '28px',
};

const messageStyle: React.CSSProperties = {
  margin: 0, fontSize: 13, fontWeight: 400, color: '#6B5E52',
  lineHeight: '20px', textAlign: 'center',
};

const senderCardStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
  backgroundColor: '#FAFAF8', border: '1px solid #E8DED4', borderRadius: 8,
  width: '100%', boxSizing: 'border-box',
};

const avatarStyle: React.CSSProperties = {
  width: 28, height: 28, borderRadius: 14, backgroundColor: '#D97645',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 10, fontWeight: 600, color: '#FFFFFF', flexShrink: 0,
  lineHeight: '12px',
};

const senderNameStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 500, color: '#3D3229', lineHeight: '16px',
};

const senderEmailStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 400, color: '#9E9189', lineHeight: '14px',
};

const statusRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  paddingTop: 4, width: '100%',
};

const statusLabelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 400, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: '#B5AA9C', lineHeight: '14px',
};
